import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
    Zap, Clock, AlertTriangle, CheckCircle2, Download,
    Power, PowerOff, Factory, RefreshCw, X, ChevronDown, ChevronUp,
    Bell, BellOff, BarChart3, Activity, Loader2, Wifi, WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    BarChart, Bar, AreaChart, Area, LineChart, Line, XAxis, YAxis,
    CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { getWebSocketClient } from '../../utils/websocket-client';
import { API_BASE } from '../../utils/api-base';

// --- Types ---
export interface MachineEnergyMonitorProps {
    clientId: string;
    token: string;
    permissions: any;
    userRole?: string;
    pollingIntervalMs?: number;
    onDataExport?: (machines: MeterState[]) => void;
    onOpenDetailedReport?: () => void;
}

interface EnergyTag {
    id: string;
    name: string;
    address: number;
    unit: string;
    divider?: number;
    regCount?: number;
    valueType?: string;
    registerType?: string;
    wordOrder?: string;
}

interface MeterSession {
    type: 'online' | 'offline';
    from: number;
    to: number | null;
    durationMs: number;
}

interface DailyUsagePoint {
    date: string;
    kwh: number;
    uptimeMs?: number;
    downtimeMs?: number;
}

interface BudgetSettings {
    unitRatePerKwh: number;
    currencyCode: string;
    meterBudgets: Record<string, number>;
}

export interface MeterState {
    id: string;
    name: string;
    description: string;
    serialPort: string;
    baudRate: number;
    dataBits: number;
    stopBits: number;
    parity: string;
    slaveId: number;
    isActive: boolean;
    tags: EnergyTag[];
    liveValues: Record<string, number | null>;
    peakValues: Record<string, number>;
    connected: boolean;
    reconnecting: boolean;
    lastUpdated: number | null;
    uptimeMs: number;
    downtimeMs: number;
    lastOnlineAt: number | null;
    lastOfflineAt: number | null;
    lastDownDurationMs: number;
    currentStateSince: number;
    sessions: MeterSession[];
    history: { ts: string; kw: number }[];
}

// --- Helpers ---
const fmtDur = (ms: number) => {
    if (!ms || ms < 0) return '0m';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
};

const fmtTs = (ts: number | null) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtDateTime = (ts: number | null) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-IN', {
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
};

const fmtKwh = (value: number | null | undefined) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 5,
    }).format(value);
};

const STATUS_COLOR: Record<string, string> = {
    online: '#22c55e',
    offline: '#ef4444',
};

const METER_COLORS = [
    '#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b', '#ec4899', '#06b6d4',
    '#f97316', '#a855f7', '#14b8a6', '#84cc16',
];
const DAY_MS = 24 * 60 * 60 * 1000;

const normalizeTagName = (value: string) => String(value || '').trim().toLowerCase();

const getPreferredTagValue = (
    meter: MeterState,
    preferredNames: string[],
    predicate?: (tag: EnergyTag) => boolean,
): number | null => {
    const preferredNameSet = new Set(preferredNames.map(normalizeTagName));
    const tags = meter.tags.filter((tag) => !predicate || predicate(tag));

    const preferredTag = tags.find((tag) => preferredNameSet.has(normalizeTagName(tag.name)));
    if (preferredTag) {
        const value = meter.liveValues[preferredTag.name];
        return typeof value === 'number' ? value : null;
    }

    const firstTag = tags.find((tag) => typeof meter.liveValues[tag.name] === 'number');
    if (!firstTag) {
        return null;
    }

    return meter.liveValues[firstTag.name] as number;
};

const getPowerKw = (m: MeterState): number => {
    const preferredPower = getPreferredTagValue(
        m,
        ['Total Active Power', 'Total Active Power (kW)', 'Active Power', 'Power'],
        (tag) => tag.unit === 'kW',
    );
    if (preferredPower !== null) {
        return preferredPower;
    }

    for (const [k, v] of Object.entries(m.liveValues)) {
        const normalizedName = normalizeTagName(k);
        if (
            typeof v === 'number'
            && normalizedName.includes('power')
            && !normalizedName.includes('energy')
            && !normalizedName.includes('kwh')
        ) {
            return v;
        }
    }

    return 0;
};

const getVoltage = (m: MeterState): number | null => {
    return getPreferredTagValue(
        m,
        ['Avg Voltage (L-N)', 'Avg Voltage (L-L)', 'Voltage V1N', 'Voltage V2N', 'Voltage V3N', 'Voltage V12', 'Voltage V23', 'Voltage V31'],
        (tag) => tag.unit === 'V',
    );
};

const getCurrent = (m: MeterState): number | null => {
    return getPreferredTagValue(
        m,
        ['Avg Current', 'Avg Current Demand', 'Phase 1 Current', 'Phase 2 Current', 'Phase 3 Current', 'Neutral Current'],
        (tag) => tag.unit === 'A',
    );
};

const getEnergy = (m: MeterState): number | null => {
    return getPreferredTagValue(
        m,
        ['Active Energy Import', 'Total Active Energy', 'Energy'],
        (tag) => tag.unit === 'kWh',
    );
};

const getPF = (m: MeterState): number | null => {
    const byName = m.tags.find((t) => {
        const normalized = normalizeTagName(t.name);
        return normalized === 'total pf' || normalized === 'power factor' || normalized === 'pf';
    });
    if (byName) {
        const value = m.liveValues[byName.name];
        return typeof value === 'number' ? value : null;
    }

    const byUnit = m.tags.find((t) => t.unit === '' || t.unit === 'PF' || t.unit === 'pf');
    if (!byUnit) {
        return null;
    }

    const value = m.liveValues[byUnit.name];
    return typeof value === 'number' ? value : null;
};

const meterStatus = (m: MeterState): 'online' | 'offline' => {
    if (m.connected) return 'online';
    return 'offline';
};

const elapsedSince = (since: number, now: number): number => {
    if (!Number.isFinite(since)) return 0;
    return Math.max(0, now - since);
};

const settleAccumulatedDurations = (meter: MeterState, now: number): { uptimeMs: number; downtimeMs: number } => {
    const elapsed = elapsedSince(meter.currentStateSince, now);
    if (elapsed <= 0) {
        return { uptimeMs: meter.uptimeMs, downtimeMs: meter.downtimeMs };
    }

    if (meter.connected) {
        return { uptimeMs: meter.uptimeMs + elapsed, downtimeMs: meter.downtimeMs };
    }

    return { uptimeMs: meter.uptimeMs, downtimeMs: meter.downtimeMs + elapsed };
};

const getDayStartMs = (timestamp: number): number => {
    const date = new Date(timestamp);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
};

const getRangeDurations = (
    meter: MeterState,
    rangeStartMs: number,
    rangeEndMs: number,
    now: number
): { uptimeMs: number; downtimeMs: number } => {
    if (!Number.isFinite(rangeStartMs) || !Number.isFinite(rangeEndMs) || rangeEndMs <= rangeStartMs) {
        return { uptimeMs: 0, downtimeMs: 0 };
    }

    const intervals = (Array.isArray(meter.sessions) && meter.sessions.length > 0
        ? meter.sessions.map((session) => ({
            type: session.type,
            from: session.from,
            to: session.to ?? now,
        }))
        : [{
            type: meter.connected ? 'online' as const : 'offline' as const,
            from: meter.currentStateSince,
            to: now,
        }])
        .filter((interval) => Number.isFinite(interval.from) && Number.isFinite(interval.to) && interval.to > interval.from)
        .sort((a, b) => a.from - b.from);

    let uptimeMs = 0;
    let downtimeMs = 0;
    let coveredMs = 0;

    for (const interval of intervals) {
        const overlapStart = Math.max(rangeStartMs, interval.from);
        const overlapEnd = Math.min(rangeEndMs, interval.to);
        if (overlapEnd <= overlapStart) continue;

        const overlapMs = overlapEnd - overlapStart;
        coveredMs += overlapMs;
        if (interval.type === 'online') {
            uptimeMs += overlapMs;
        } else {
            downtimeMs += overlapMs;
        }
    }

    const totalRangeMs = rangeEndMs - rangeStartMs;
    if (coveredMs < totalRangeMs) {
        // If we have partial session coverage for the range, treat unknown span as downtime.
        downtimeMs += (totalRangeMs - coveredMs);
    }

    return { uptimeMs, downtimeMs };
};

const getDailyDurations = (usagePoints: DailyUsagePoint[], now: number) => {
    const todayKey = toDayKey(now);
    const yesterdayKey = toDayKey(new Date(getDayStartMs(now) - DAY_MS));

    const today = {
        uptimeMs: usagePoints.find(p => p.date === todayKey)?.uptimeMs ?? 0,
        downtimeMs: usagePoints.find(p => p.date === todayKey)?.downtimeMs ?? 0
    };
    const yesterday = {
        uptimeMs: usagePoints.find(p => p.date === yesterdayKey)?.uptimeMs ?? 0,
        downtimeMs: usagePoints.find(p => p.date === yesterdayKey)?.downtimeMs ?? 0
    };

    const todayTotal = today.uptimeMs + today.downtimeMs;
    const yesterdayTotal = yesterday.uptimeMs + yesterday.downtimeMs;

    return {
        today,
        yesterday,
        todayUptimePct: todayTotal > 0 ? (today.uptimeMs / todayTotal) * 100 : 0,
        yesterdayUptimePct: yesterdayTotal > 0 ? (yesterday.uptimeMs / yesterdayTotal) * 100 : 0,
    };
};

const getDisplayUptimeMs = (usagePoints: DailyUsagePoint[], now: number): number => {
    return getDailyDurations(usagePoints, now).today.uptimeMs;
};

const getDisplayDowntimeMs = (usagePoints: DailyUsagePoint[], now: number): number => {
    return getDailyDurations(usagePoints, now).today.downtimeMs;
};

// Fast status detection for instant bridge stop/start visibility in client portal.
const CURRENT_API_FRESHNESS_WINDOW_MS = 4000;
const HISTORY_LOOKBACK_DAYS = 70;
const DEFAULT_BUDGET_SETTINGS: BudgetSettings = {
    unitRatePerKwh: 0,
    currencyCode: 'BDT',
    meterBudgets: {},
};

const toDayKey = (value: number | string | Date): string => {
    const date = value instanceof Date ? value : new Date(value);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const parseDayKeyToStartMs = (dayKey: string, fallbackTs: number): number => {
    const match = String(dayKey || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return getDayStartMs(fallbackTs);
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const parsed = new Date(year, month, day, 0, 0, 0, 0).getTime();
    return Number.isFinite(parsed) ? parsed : getDayStartMs(fallbackTs);
};

const rollupDailyKwhUsage = (rawReadings: any[]): DailyUsagePoint[] => {
    const parsed = (rawReadings || [])
        .map((reading) => ({
            tagName: String(reading?.tagName || reading?.name || ''),
            unit: String(reading?.unit || '').toLowerCase(),
            value: Number(reading?.value),
            timestamp: reading?.timestamp,
        }))
        .filter((reading) => reading.unit === 'kwh' && Number.isFinite(reading.value) && reading.timestamp)
        .sort((a, b) => Date.parse(String(a.timestamp)) - Date.parse(String(b.timestamp)));

    if (parsed.length === 0) return [];

    const preferred = parsed.filter((reading) => /import/i.test(reading.tagName));
    const source = preferred.length > 0 ? preferred : parsed;

    const daily = new Map<string, { min: number; max: number }>();
    source.forEach((reading) => {
        const dayKey = toDayKey(String(reading.timestamp));
        const current = daily.get(dayKey);
        if (!current) {
            daily.set(dayKey, { min: reading.value, max: reading.value });
            return;
        }
        daily.set(dayKey, {
            min: Math.min(current.min, reading.value),
            max: Math.max(current.max, reading.value),
        });
    });

    return Array.from(daily.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, range]) => ({
            date,
            kwh: Math.max(0, range.max - range.min),
        }));
};

interface NormalizedMeterReading {
    ts: number;
    value: number;
    tagName: string;
    unit: string;
}

const normalizeMeterReadings = (rawReadings: any[]): NormalizedMeterReading[] => {
    return (rawReadings || [])
        .map((reading) => {
            const ts = Date.parse(String(reading?.timestamp || ''));
            const value = Number(reading?.value);
            return {
                ts,
                value,
                tagName: String(reading?.tagName || reading?.name || ''),
                unit: String(reading?.unit || '').toLowerCase(),
            };
        })
        .filter((reading) => Number.isFinite(reading.ts) && Number.isFinite(reading.value))
        .sort((a, b) => a.ts - b.ts);
};

const buildPersistedMeterState = (
    meter: MeterState,
    rawReadings: any[],
    now: number,
    pollingIntervalMs: number | undefined
): Partial<MeterState> => {
    const normalized = normalizeMeterReadings(rawReadings);
    if (normalized.length === 0) {
        return {
            connected: false,
            reconnecting: false,
            lastUpdated: meter.lastUpdated,
            history: meter.history,
        };
    }

    const effectivePollingMs = Number(pollingIntervalMs) || 5000;
    const groupingMs = Math.max(1000, Math.floor(effectivePollingMs * 0.6));
    const gapThresholdMs = Math.max(15000, effectivePollingMs * 3);
    const freshnessWindowMs = Math.max(CURRENT_API_FRESHNESS_WINDOW_MS, effectivePollingMs * 2 + 2000);

    const latestByTag = new Map<string, { ts: number; value: number }>();
    const peakByTag = new Map<string, number>();
    for (const reading of normalized) {
        latestByTag.set(reading.tagName, { ts: reading.ts, value: reading.value });
        const prevPeak = peakByTag.get(reading.tagName);
        if (prevPeak === undefined || reading.value > prevPeak) {
            peakByTag.set(reading.tagName, reading.value);
        }
    }

    const mergedLiveValues = { ...meter.liveValues };
    for (const [tagName, latest] of latestByTag.entries()) {
        mergedLiveValues[tagName] = latest.value;
    }

    const mergedPeakValues = { ...meter.peakValues };
    for (const [tagName, peak] of peakByTag.entries()) {
        const previous = mergedPeakValues[tagName];
        mergedPeakValues[tagName] = typeof previous === 'number' ? Math.max(previous, peak) : peak;
    }

    const powerTagName = meter.tags.find((tag) => tag.unit === 'kW')?.name;
    const powerReadings = normalized.filter((reading) => {
        if (powerTagName) return reading.tagName === powerTagName;
        if (reading.unit === 'kw') return true;
        const tagKey = reading.tagName.toLowerCase();
        return tagKey.includes('active power') || tagKey.includes('power');
    });

    const history = powerReadings.slice(-240).map((reading) => ({
        ts: new Date(reading.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        kw: +reading.value.toFixed(2),
    }));

    const samples: number[] = [];
    for (const reading of normalized) {
        if (samples.length === 0) {
            samples.push(reading.ts);
            continue;
        }
        const lastIdx = samples.length - 1;
        if ((reading.ts - samples[lastIdx]) >= groupingMs) {
            samples.push(reading.ts);
        } else {
            samples[lastIdx] = reading.ts;
        }
    }

    if (samples.length === 0) {
        return {
            liveValues: mergedLiveValues,
            peakValues: mergedPeakValues,
            lastUpdated: meter.lastUpdated,
            history,
            connected: false,
            reconnecting: false,
        };
    }

    const sessions: MeterSession[] = [];
    let baseUptimeMs = 0;
    let baseDowntimeMs = 0;

    let onlineStart = samples[0];
    let prevSample = samples[0];
    for (let idx = 1; idx < samples.length; idx += 1) {
        const currentSample = samples[idx];
        const gapMs = currentSample - prevSample;

        if (gapMs > gapThresholdMs) {
            const onlineDuration = Math.max(0, prevSample - onlineStart);
            sessions.push({ type: 'online', from: onlineStart, to: prevSample, durationMs: onlineDuration });
            baseUptimeMs += onlineDuration;

            sessions.push({ type: 'offline', from: prevSample, to: currentSample, durationMs: gapMs });
            baseDowntimeMs += gapMs;

            onlineStart = currentSample;
        }

        prevSample = currentSample;
    }

    const latestSampleTs = samples[samples.length - 1];
    const isOnlineNow = (now - latestSampleTs) <= freshnessWindowMs;

    let currentStateSince = latestSampleTs;
    if (isOnlineNow) {
        sessions.push({
            type: 'online',
            from: onlineStart,
            to: null,
            durationMs: Math.max(0, now - onlineStart),
        });
        currentStateSince = onlineStart;
    } else {
        const onlineDuration = Math.max(0, latestSampleTs - onlineStart);
        sessions.push({
            type: 'online',
            from: onlineStart,
            to: latestSampleTs,
            durationMs: onlineDuration,
        });
        baseUptimeMs += onlineDuration;

        sessions.push({
            type: 'offline',
            from: latestSampleTs,
            to: null,
            durationMs: Math.max(0, now - latestSampleTs),
        });
        currentStateSince = latestSampleTs;
    }

    const latestOnline = [...sessions].reverse().find((session) => session.type === 'online');
    const latestOffline = [...sessions].reverse().find((session) => session.type === 'offline');
    const latestCompletedOffline = [...sessions].reverse().find((session) => session.type === 'offline' && session.to !== null);

    const lastDownDurationMs = isOnlineNow
        ? (latestCompletedOffline?.durationMs || 0)
        : Math.max(0, now - currentStateSince);

    return {
        liveValues: mergedLiveValues,
        peakValues: mergedPeakValues,
        history,
        connected: isOnlineNow,
        reconnecting: !isOnlineNow,
        lastUpdated: latestSampleTs,
        sessions: sessions.slice(-80),
        uptimeMs: baseUptimeMs,
        downtimeMs: baseDowntimeMs,
        lastOnlineAt: latestOnline?.from ?? null,
        lastOfflineAt: latestOffline?.from ?? null,
        lastDownDurationMs,
        currentStateSince,
    };
};

// --- Component ---
export function MachineEnergyMonitor({ clientId, token, permissions, userRole, pollingIntervalMs, onDataExport, onOpenDetailedReport }: MachineEnergyMonitorProps) {
    const [meters, setMeters] = useState<MeterState[]>([]);
    const [loading, setLoading] = useState(true);
    const [meterRealtimeAllowed, setMeterRealtimeAllowed] = useState<boolean | null>(null);
    const [nowTick, setNowTick] = useState<number>(() => Date.now());
    const [comparisonWindow, setComparisonWindow] = useState<'weekly' | 'monthly'>('weekly');
    const [reportCompareDate, setReportCompareDate] = useState<string>(() => toDayKey(Date.now()));
    const [dailyUsageByMeter, setDailyUsageByMeter] = useState<Record<string, DailyUsagePoint[]>>({});
    const [historyLoading, setHistoryLoading] = useState(false);
    const [budgetSettings, setBudgetSettings] = useState<BudgetSettings>(DEFAULT_BUDGET_SETTINGS);
    const [budgetSettingsLoading, setBudgetSettingsLoading] = useState(false);
    const [budgetSettingsSaving, setBudgetSettingsSaving] = useState(false);
    const [budgetSettingsDirty, setBudgetSettingsDirty] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'report'>('overview');
    const [expandedCard, setExpandedCard] = useState<string | null>(null);
    const [showNotif, setShowNotif] = useState(false);
    const [notifMuted, setNotifMuted] = useState(false);
    const [alerts, setAlerts] = useState<{ id: string; msg: string; type: 'down' | 'up' | 'energy'; ts: number; read: boolean }[]>([]);
    const prevStatuses = useRef<Record<string, 'online' | 'offline'>>({});
    const metersRef = useRef<MeterState[]>([]);
    const wsClient = useRef<any>(null);
    const budgetSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wsDisconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Grace period before showing a meter as offline — prevents flapping when
    // multiple meters share a single USB Modbus adapter and brief poll failures
    // trigger reconnect cycles that resolve within seconds.
    const meterOfflineTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const METER_OFFLINE_GRACE_MS = 30_000;

    useEffect(() => {
        metersRef.current = meters;
    }, [meters]);

    // Shared grace-period helpers — every code path that can report a meter as offline
    // (direct WebSocket status/connect-result events, and the REST "current value" fallback
    // poll used when the portal has no direct bridge WebSocket) routes through these so a
    // single missed read never instantly flips status or fires a false alert.
    const applyMeterOffline = useCallback((meterId: string) => {
        meterOfflineTimersRef.current.delete(meterId);
        const now = Date.now();
        setMeters(prev => prev.map((m) => {
            if (m.id !== meterId || !m.connected) return m;
            const settled = settleAccumulatedDurations(m, now);
            return {
                ...m,
                connected: false,
                reconnecting: true,
                liveValues: {},
                uptimeMs: settled.uptimeMs,
                downtimeMs: settled.downtimeMs,
                lastOfflineAt: now,
                currentStateSince: now,
                sessions: [
                    ...m.sessions.map((s, si) => si === m.sessions.length - 1 && s.to === null ? { ...s, to: now, durationMs: now - s.from } : s),
                    { type: 'offline' as const, from: now, to: null, durationMs: 0 },
                ].slice(-40),
            };
        }));
    }, []);

    const scheduleMeterOffline = useCallback((meterId: string) => {
        if (!meterOfflineTimersRef.current.has(meterId)) {
            const t = setTimeout(() => applyMeterOffline(meterId), METER_OFFLINE_GRACE_MS);
            meterOfflineTimersRef.current.set(meterId, t);
        }
    }, [applyMeterOffline]);

    const cancelMeterOfflineTimer = useCallback((meterId: string) => {
        const pending = meterOfflineTimersRef.current.get(meterId);
        if (pending) {
            clearTimeout(pending);
            meterOfflineTimersRef.current.delete(meterId);
        }
    }, []);

    const normalizedUserRole = String(userRole || '').toLowerCase();
    const canEditBudget = useMemo(() => {
        if (permissions?.settings?.edit) return true;
        return ['admin', 'client_admin', 'super_admin', 'owner', 'supervisor'].includes(normalizedUserRole);
    }, [permissions?.settings?.edit, normalizedUserRole]);

    useEffect(() => {
        if (!token || !clientId) return;

        let cancelled = false;

        const loadBudgetSettings = async () => {
            try {
                setBudgetSettingsLoading(true);
                const response = await fetch(`${API_BASE}/api/clients/${clientId}/energy-budget-settings`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!response.ok) {
                    throw new Error('Failed to load budget settings');
                }

                const body = await response.json();
                const settings = body?.settings || {};

                if (cancelled) return;
                setBudgetSettings({
                    unitRatePerKwh: Math.max(0, Number(settings.unitRatePerKwh) || 0),
                    currencyCode: String(settings.currencyCode || 'BDT').toUpperCase().slice(0, 3),
                    meterBudgets: settings.meterBudgets && typeof settings.meterBudgets === 'object' ? settings.meterBudgets : {},
                });
                setBudgetSettingsDirty(false);
            } catch (error) {
                if (!cancelled) {
                    setBudgetSettings(DEFAULT_BUDGET_SETTINGS);
                    setBudgetSettingsDirty(false);
                    toast.error('Failed to load shared budget settings');
                }
            } finally {
                if (!cancelled) {
                    setBudgetSettingsLoading(false);
                }
            }
        };

        loadBudgetSettings();
        return () => {
            cancelled = true;
        };
    }, [clientId, token]);

    useEffect(() => {
        if (!token || !clientId) return;
        if (!budgetSettingsDirty || !canEditBudget || budgetSettingsLoading) return;

        if (budgetSaveTimerRef.current) {
            clearTimeout(budgetSaveTimerRef.current);
            budgetSaveTimerRef.current = null;
        }

        budgetSaveTimerRef.current = setTimeout(async () => {
            try {
                setBudgetSettingsSaving(true);
                const response = await fetch(`${API_BASE}/api/clients/${clientId}/energy-budget-settings`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ settings: budgetSettings }),
                });

                if (!response.ok) {
                    throw new Error('Failed to save budget settings');
                }

                setBudgetSettingsDirty(false);
            } catch {
                toast.error('Could not save budget settings to server');
            } finally {
                setBudgetSettingsSaving(false);
            }
        }, 600);

        return () => {
            if (budgetSaveTimerRef.current) {
                clearTimeout(budgetSaveTimerRef.current);
                budgetSaveTimerRef.current = null;
            }
        };
    }, [budgetSettings, budgetSettingsDirty, budgetSettingsLoading, canEditBudget, clientId, token]);

    // Load energy meters from API
    const loadMeters = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE}/api/clients/${clientId}/energy-meters`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Failed to fetch energy meters');
            const data = await res.json();
            const apiMeters: any[] = data.meters || [];
            const now = Date.now();

            // Fetch runtime status in parallel so we can set correct initial connected state
            // and avoid the brief "offline" flash when returning to this screen.
            let runtimeByMeter = new Map<string, any>();
            try {
                const runtimeRes = await fetch(`${API_BASE}/api/clients/${clientId}/device-runtime-status`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (runtimeRes.ok) {
                    const runtimeBody = await runtimeRes.json();
                    const runtimeStatuses = Array.isArray(runtimeBody?.statuses) ? runtimeBody.statuses : [];
                    runtimeByMeter = new Map<string, any>(
                        runtimeStatuses
                            .filter((s: any) => s?.deviceType === 'energy-meter' || s?.connectionType === 'energy-meter')
                            .map((s: any) => [String(s.deviceId || s.plcId || ''), s])
                    );
                }
            } catch { /* non-critical — fall back to offline defaults */ }

            setMeters(apiMeters.map((m) => {
                const runtime = runtimeByMeter.get(m.id);
                // No runtime-status entry at all just means "no signal yet" (e.g. the status
                // API hasn't caught up), not a confirmed disconnect — default optimistic here
                // so the card doesn't flash OFFLINE (and record a fake down period) on every
                // screen open. A genuine runtime.connected === false IS a real signal and is
                // still honored immediately.
                const isConnected = runtime ? !!runtime.connected : true;
                const isReconnecting = runtime ? !!runtime.reconnecting : false;
                const initialSession = isConnected
                    ? { type: 'online' as const, from: now, to: null, durationMs: 0 }
                    : { type: 'offline' as const, from: now, to: null, durationMs: 0 };
                return {
                    id: m.id,
                    name: m.name,
                    description: m.description || '',
                    serialPort: m.serialPort || '',
                    baudRate: Number(m.baudRate) || 9600,
                    dataBits: Number(m.dataBits) || 8,
                    stopBits: Number(m.stopBits) || 1,
                    parity: m.parity || 'none',
                    slaveId: Number(m.slaveId) || 1,
                    isActive: !!m.isActive,
                    tags: (m.tags || []).map((t: any) => ({
                        id: t.id,
                        name: t.name,
                        address: Number(t.address),
                        unit: t.unit || '',
                        divider: Number(t.divider) || 1,
                        regCount: Math.max(1, Number(t.regCount) || (Number(t.address) >= 1300 ? 2 : 1)),
                        valueType: String(t.valueType || (Number(t.address) >= 1300 ? 'uint32' : 'uint16')),
                        registerType: String(t.registerType || 'holding'),
                        wordOrder: String(t.wordOrder || 'high-low'),
                    })),
                    liveValues: {},
                    peakValues: {},
                    connected: isConnected,
                    reconnecting: isReconnecting,
                    lastUpdated: null,
                    uptimeMs: 0,
                    downtimeMs: 0,
                    lastOnlineAt: isConnected ? now : null,
                    lastOfflineAt: isConnected ? null : now,
                    lastDownDurationMs: 0,
                    currentStateSince: now,
                    sessions: [initialSession],
                    history: [],
                };
            }));
            if (apiMeters.length > 0) setSelectedId(apiMeters[0].id);
        } catch (err: any) {
            console.error('Failed to load energy meters:', err);
            toast.error('Failed to load energy meters');
        } finally {
            setLoading(false);
        }
    }, [clientId, token]);

    useEffect(() => { loadMeters(); }, [loadMeters]);

    // Determine whether we have a direct WebSocket bridge (vs VPS/SSE relay mode).
    useEffect(() => {
        if (!token || !clientId) return;
        let cancelled = false;
        const ws = getWebSocketClient(token, { clientId, pollingIntervalMs });
        ws.canUseRealtimeControl()
            .then((allowed: boolean) => { if (!cancelled) setMeterRealtimeAllowed(allowed ?? false); })
            .catch(() => { if (!cancelled) setMeterRealtimeAllowed(true); });
        return () => { cancelled = true; };
    }, [clientId, token, pollingIntervalMs]);

    const meterIdKey = meters.map((meter) => meter.id).sort().join('|');

    useEffect(() => {
        if (!token || !clientId || meters.length === 0) {
            setDailyUsageByMeter({});
            return;
        }

        let cancelled = false;

        const loadHistoricalUsage = async () => {
            try {
                setHistoryLoading(true);
                const meterUsageEntries = await Promise.all(
                    meters.map(async (meter) => {
                        try {
                            const response = await fetch(
                                `${API_BASE}/api/clients/${clientId}/energy-meters/${meter.id}/daily-usage?days=${HISTORY_LOOKBACK_DAYS}`,
                                { headers: { Authorization: `Bearer ${token}` } }
                            );
                            if (!response.ok) {
                                return {
                                    meterId: meter.id,
                                    dailyUsage: [] as DailyUsagePoint[],
                                    readings: [] as any[],
                                };
                            }

                            const body = await response.json();
                            const dailyUsage = Array.isArray(body?.dailyUsage) ? body.dailyUsage : [];
                            return {
                                meterId: meter.id,
                                dailyUsage,
                                readings: [],
                            };
                        } catch {
                            return {
                                meterId: meter.id,
                                dailyUsage: [] as DailyUsagePoint[],
                                readings: [] as any[],
                            };
                        }
                    })
                );

                if (cancelled) return;
                const usageByMeter = Object.fromEntries(
                    meterUsageEntries.map((entry) => [entry.meterId, entry.dailyUsage])
                );
                setDailyUsageByMeter(usageByMeter);

                const meterSnapshots = new Map(meterUsageEntries.map((entry) => [entry.meterId, entry]));
                const hydrateNow = Date.now();
                setMeters((prev) => prev.map((meter) => {
                    const entry = meterSnapshots.get(meter.id);
                    if (!entry) return meter;

                    const persistedState = buildPersistedMeterState(meter, entry.readings, hydrateNow, pollingIntervalMs);
                    if (
                        meter.lastUpdated
                        && persistedState.lastUpdated
                        && meter.lastUpdated > persistedState.lastUpdated
                    ) {
                        return meter;
                    }

                    return {
                        ...meter,
                        ...persistedState,
                        // Never override connected/reconnecting here — loadMeters already set
                        // the correct initial state via device-runtime-status. Overriding with
                        // buildPersistedMeterState (which has no readings) causes a brief
                        // offline flash every time this screen is visited.
                        connected: meter.connected,
                        reconnecting: meter.reconnecting,
                    };
                }));

                try {
                    const runtimeResponse = await fetch(`${API_BASE}/api/clients/${clientId}/device-runtime-status`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (runtimeResponse.ok && !cancelled) {
                        const runtimeBody = await runtimeResponse.json();
                        const runtimeStatuses = Array.isArray(runtimeBody?.statuses) ? runtimeBody.statuses : [];
                        const runtimeByMeter = new Map<string, any>(
                            runtimeStatuses
                                .filter((status: any) => status?.deviceType === 'energy-meter' || status?.connectionType === 'energy-meter')
                                .map((status: any) => [String(status.deviceId || status.plcId || ''), status])
                        );

                        if (runtimeByMeter.size > 0) {
                            const syncNow = Date.now();
                            setMeters((prev) => prev.map((meter) => {
                                const runtime = runtimeByMeter.get(meter.id);
                                if (!runtime) return meter;

                                const isConnected = !!runtime.connected;
                                const isReconnecting = !!runtime.reconnecting;
                                const eventAtMs = Date.parse(runtime.lastEventAt || '');
                                const dataAtMs = Date.parse(runtime.lastDataAt || '');
                                const nextCurrentStateSince = Number.isFinite(eventAtMs) ? eventAtMs : meter.currentStateSince;
                                const nextLastUpdated = Number.isFinite(dataAtMs) ? dataAtMs : meter.lastUpdated;

                                if (isConnected) {
                                    const nextLastOnlineAt = Number.isFinite(eventAtMs) ? eventAtMs : meter.lastOnlineAt;
                                    const nextLastDownDurationMs =
                                        nextLastOnlineAt !== null && meter.lastOfflineAt !== null
                                            ? Math.max(0, nextLastOnlineAt - meter.lastOfflineAt)
                                            : meter.lastDownDurationMs;
                                    return {
                                        ...meter,
                                        connected: true,
                                        reconnecting: isReconnecting,
                                        currentStateSince: nextCurrentStateSince,
                                        lastUpdated: nextLastUpdated,
                                        lastOnlineAt: nextLastOnlineAt,
                                        lastDownDurationMs: nextLastDownDurationMs,
                                    };
                                }

                                const nextLastOfflineAt = Number.isFinite(eventAtMs) ? eventAtMs : meter.lastOfflineAt;
                                const nextLastDownDurationMs =
                                    nextLastOfflineAt !== null
                                        ? Math.max(0, syncNow - nextLastOfflineAt)
                                        : meter.lastDownDurationMs;
                                return {
                                    ...meter,
                                    connected: false,
                                    reconnecting: isReconnecting,
                                    currentStateSince: nextCurrentStateSince,
                                    lastUpdated: nextLastUpdated,
                                    lastOfflineAt: nextLastOfflineAt,
                                    lastDownDurationMs: nextLastDownDurationMs,
                                };
                            }));
                        }
                    }
                } catch {
                    // Keep meter UI functional even if runtime-status fetch fails.
                }
            } finally {
                if (!cancelled) {
                    setHistoryLoading(false);
                }
            }
        };

        loadHistoricalUsage();
        return () => {
            cancelled = true;
        };
    }, [clientId, token, meterIdKey, pollingIntervalMs]);

    const handleBudgetSettingChange = (patch: Partial<BudgetSettings>) => {
        setBudgetSettings((prev) => ({ ...prev, ...patch }));
        setBudgetSettingsDirty(true);
    };

    const handleMeterBudgetChange = (meterId: string, value: string) => {
        const parsed = Number(value);
        setBudgetSettings((prev) => ({
            ...prev,
            meterBudgets: {
                ...prev.meterBudgets,
                [meterId]: Number.isFinite(parsed) ? Math.max(0, parsed) : 0,
            },
        }));
        setBudgetSettingsDirty(true);
    };

    const buildMeterBridgeConfig = (m: MeterState) => ({
        id: m.id,
        clientId,
        name: m.name,
        connectionType: 'energy-meter',
        serialPort: m.serialPort,
        baudRate: m.baudRate,
        dataBits: m.dataBits,
        stopBits: m.stopBits,
        parity: m.parity,
        slaveId: m.slaveId,
        tags: m.tags.map(t => ({
            id: t.id,
            name: t.name,
            address: t.address,
            unit: t.unit,
            divider: t.divider ?? 1,
            regCount: t.regCount ?? (t.address >= 1300 ? 2 : 1),
            valueType: t.valueType ?? (t.address >= 1300 ? 'uint32' : 'uint16'),
            dataType: t.registerType ?? 'holding',
            registerType: t.registerType ?? 'holding',
            wordOrder: t.wordOrder ?? 'high-low',
        })),
    });
    const meterSubscriptionKey = meters
        .map((m) => [
            m.id,
            m.isActive,
            m.name,
            m.serialPort,
            m.baudRate,
            m.dataBits,
            m.stopBits,
            m.parity,
            m.slaveId,
            m.tags.map((t) => `${t.id}:${t.name}:${t.address}:${t.unit}`).join(','),
        ].join(':'))
        .join('|');

    // Connect to bridge via WebSocket once meters are loaded.
    useEffect(() => {
        if (meters.length === 0) return;

        const ws = getWebSocketClient(token, { clientId, pollingIntervalMs });
        wsClient.current = ws;

        const subscribeMeters = () => {
            // In VPS/SSE mode the bridge manages all device connections itself.
            // Sending connectPLC would trigger a reconnect cycle causing a brief offline flash.
            if (meterRealtimeAllowed !== true) return;
            metersRef.current.forEach((m) => {
                if (!m.isActive) return;
                // Skip meters the bridge already reports as connected — resending connect_plc
                // on every screen mount is unnecessary chatter on a shared RS-485 bus and only
                // needs to happen for meters that aren't actually up yet.
                if (m.connected) return;
                ws.connectPLC(buildMeterBridgeConfig(m));
            });
        };

        ws.on('connected', subscribeMeters);
        ws.connect();
        if (ws.isConnected()) {
            subscribeMeters();
        }

        return () => {
            ws.off('connected', subscribeMeters);
        };
    }, [meterSubscriptionKey, token, clientId, pollingIntervalMs, meterRealtimeAllowed]);

    // WebSocket live data handler
    useEffect(() => {
        const ws = wsClient.current;
        if (!ws) return;

        const handleLiveData = (data: any) => {
            if (data.connectionType !== 'energy-meter') return;
            const meterId: string = data.plcId;
            const values: Record<string, number> = data.values || {};
            const ts = data.timestamp || new Date().toISOString();
            const timeLabel = new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

            setMeters(prev => prev.map((m) => {
                if (m.id !== meterId) return m;
                const wasConnected = m.connected;
                const now = Date.now();
                const newPeaks = { ...m.peakValues };
                for (const [k, v] of Object.entries(values)) {
                    if (typeof v === 'number' && (newPeaks[k] === undefined || v > newPeaks[k])) newPeaks[k] = v;
                }
                const powerTag = m.tags.find(t => t.unit === 'kW');
                // Fall back to first available numeric value (e.g. voltage) so the graph
                // is useful even for meters that only have V/A tags configured.
                const kwVal = powerTag
                    ? (values[powerTag.name] ?? 0)
                    : (Object.values(values).find(v => typeof v === 'number') as number ?? 0);
                const newHist = [...m.history, { ts: timeLabel, kw: +kwVal.toFixed(2) }].slice(-40);
                let newSessions = m.sessions;
                let newLastOnlineAt = m.lastOnlineAt;
                let lastDownDur = m.lastDownDurationMs;
                let newCurrentStateSince = m.currentStateSince;
                let newUptimeMs = m.uptimeMs;
                let newDowntimeMs = m.downtimeMs;
                if (!wasConnected) {
                    const settled = settleAccumulatedDurations(m, now);
                    newUptimeMs = settled.uptimeMs;
                    newDowntimeMs = settled.downtimeMs;
                    newLastOnlineAt = now;
                    newCurrentStateSince = now;
                    lastDownDur = m.lastOfflineAt ? now - m.lastOfflineAt : 0;
                    newSessions = [
                        ...m.sessions.map((s, si) => si === m.sessions.length - 1 && s.to === null ? { ...s, to: now, durationMs: now - s.from } : s),
                        { type: 'online' as const, from: now, to: null, durationMs: 0 },
                    ].slice(-40);
                }
                return {
                    ...m,
                    connected: true,
                    reconnecting: false,
                    liveValues: { ...m.liveValues, ...values },
                    peakValues: newPeaks,
                    lastUpdated: now,
                    history: newHist,
                    lastOnlineAt: newLastOnlineAt,
                    lastDownDurationMs: lastDownDur,
                    currentStateSince: newCurrentStateSince,
                    sessions: newSessions,
                    uptimeMs: newUptimeMs,
                    downtimeMs: newDowntimeMs,
                };
            }));
        };

        const handleStatusUpdate = (data: any) => {
            if (data.connectionType !== 'energy-meter') return;
            const meterId: string = data.plcId;
            const isConnected: boolean = !!data.connected;
            const now = Date.now();

            if (!isConnected) {
                // Grace period: shared USB Modbus adapters cause brief reconnect cycles.
                // Only mark offline if the meter hasn't recovered within METER_OFFLINE_GRACE_MS.
                scheduleMeterOffline(meterId);
                return;
            }

            // Meter is back online — cancel any pending offline timer
            cancelMeterOfflineTimer(meterId);

            setMeters(prev => prev.map((m) => {
                if (m.id !== meterId) return m;
                if (m.connected && !m.reconnecting) return m;
                const settled = settleAccumulatedDurations(m, now);
                return {
                    ...m,
                    connected: true,
                    reconnecting: false,
                    uptimeMs: settled.uptimeMs,
                    downtimeMs: settled.downtimeMs,
                    lastOnlineAt: now,
                    lastDownDurationMs: m.lastOfflineAt ? now - m.lastOfflineAt : m.lastDownDurationMs,
                    currentStateSince: now,
                    sessions: [
                        ...m.sessions.map((s, si) => si === m.sessions.length - 1 && s.to === null ? { ...s, to: now, durationMs: now - s.from } : s),
                        { type: 'online' as const, from: now, to: null, durationMs: 0 },
                    ].slice(-40),
                };
            }));
        };

        const handleConnectionResult = (data: any) => {
            if (data.connectionType !== 'energy-meter') return;
            const meterId: string = data.plcId;
            const nextConnected = !!data.connected;
            const now = Date.now();

            if (!nextConnected) {
                // Same grace period as plc_status_update: a connect_plc response can report a
                // transient failure (e.g. shared RS-485 bus contention while sibling meters are
                // being probed) that resolves within seconds. Don't flip to offline or fire an
                // alert until the meter has actually stayed down for the grace window.
                scheduleMeterOffline(meterId);
                return;
            }

            // Meter reported connected — cancel any pending offline timer.
            cancelMeterOfflineTimer(meterId);

            setMeters(prev => prev.map((m) => {
                if (m.id !== meterId) return m;
                if (m.connected && !m.reconnecting) return m;

                const settled = settleAccumulatedDurations(m, now);
                return {
                    ...m,
                    uptimeMs: settled.uptimeMs,
                    downtimeMs: settled.downtimeMs,
                    connected: true,
                    reconnecting: false,
                    lastOnlineAt: now,
                    lastDownDurationMs: m.lastOfflineAt ? now - m.lastOfflineAt : m.lastDownDurationMs,
                    currentStateSince: now,
                    sessions: [
                        ...m.sessions.map((s, i) => i === m.sessions.length - 1 && s.to === null ? { ...s, to: now, durationMs: now - s.from } : s),
                        { type: 'online' as const, from: now, to: null, durationMs: 0 },
                    ].slice(-40),
                };
            }));
        };

        const handleSocketDisconnected = () => {
            // Don't immediately mark meters offline — brief WebSocket reconnections
            // (e.g. network glitch lasting < 10 s) should not count as meter downtime.
            if (wsDisconnectTimerRef.current) clearTimeout(wsDisconnectTimerRef.current);
            wsDisconnectTimerRef.current = setTimeout(() => {
                wsDisconnectTimerRef.current = null;
                const now = Date.now();
                setMeters((prev) => prev.map((m) => {
                    if (!m.isActive) return m;
                    if (!m.connected && m.reconnecting) return m;
                    if (!m.connected) {
                        return {
                            ...m,
                            reconnecting: true,
                            lastOfflineAt: m.lastOfflineAt || now,
                        };
                    }

                    const settled = settleAccumulatedDurations(m, now);

                    const nextSessions = m.connected
                        ? [
                            ...m.sessions.map((s, i) => i === m.sessions.length - 1 && s.to === null ? { ...s, to: now, durationMs: now - s.from } : s),
                            { type: 'offline' as const, from: now, to: null, durationMs: 0 },
                        ].slice(-40)
                        : m.sessions;

                    return {
                        ...m,
                        uptimeMs: settled.uptimeMs,
                        downtimeMs: settled.downtimeMs,
                        connected: false,
                        reconnecting: true,
                        liveValues: {},   // clear stale readings on WS disconnect
                        lastOfflineAt: now,
                        currentStateSince: now,
                        sessions: nextSessions,
                    };
                }));
            }, 10000);
        };

        const handleSocketConnected = () => {
            // WebSocket reconnected — cancel any pending disconnect timer.
            if (wsDisconnectTimerRef.current) {
                clearTimeout(wsDisconnectTimerRef.current);
                wsDisconnectTimerRef.current = null;
            }
            setMeters((prev) => prev.map((m) => (m.isActive ? { ...m, reconnecting: false } : m)));
        };

        ws.on('plc_live_data', handleLiveData);
        ws.on('plc_status_update', handleStatusUpdate);
        ws.on('plc_connection_result', handleConnectionResult);
        ws.on('disconnected', handleSocketDisconnected);
        ws.on('connected', handleSocketConnected);
        return () => {
            ws.off('plc_live_data', handleLiveData);
            ws.off('plc_status_update', handleStatusUpdate);
            ws.off('plc_connection_result', handleConnectionResult);
            ws.off('disconnected', handleSocketDisconnected);
            ws.off('connected', handleSocketConnected);
            if (wsDisconnectTimerRef.current) {
                clearTimeout(wsDisconnectTimerRef.current);
                wsDisconnectTimerRef.current = null;
            }
            // Clear any pending offline grace timers
            meterOfflineTimersRef.current.forEach(t => clearTimeout(t));
            meterOfflineTimersRef.current.clear();
        };
    }, [meters.length, scheduleMeterOffline, cancelMeterOfflineTimer]);

    // Instant disconnect watchdog: if fresh data stops for too long, mark meter offline quickly.
    useEffect(() => {
        if (meters.length === 0) return;

        // Generous stale threshold for RS485 buses with multiple meters:
        // each offline meter can hold the shared bus for up to 10 s per probe,
        // so with several meters the cycle time can be well over 30 s.
        const staleThresholdMs = Math.max(60000, (Number(pollingIntervalMs) || 5000) * 15);
        const iv = setInterval(() => {
            const now = Date.now();
            setMeters((prev) => prev.map((m) => {
                if (!m.connected) return m;
                if (!m.lastUpdated) return m;
                if ((now - m.lastUpdated) <= staleThresholdMs) return m;

                const settled = settleAccumulatedDurations(m, now);

                const nextSessions = [
                    ...m.sessions.map((s, i) => i === m.sessions.length - 1 && s.to === null ? { ...s, to: now, durationMs: now - s.from } : s),
                    { type: 'offline' as const, from: now, to: null, durationMs: 0 },
                ].slice(-40);

                return {
                    ...m,
                    uptimeMs: settled.uptimeMs,
                    downtimeMs: settled.downtimeMs,
                    connected: false,
                    reconnecting: true,
                    lastOfflineAt: now,
                    currentStateSince: now,
                    sessions: nextSessions,
                };
            }));
        }, 1000);

        return () => clearInterval(iv);
    }, [meters.length, pollingIntervalMs]);

    // Poll persisted "current" values so the portal still reflects saved readings
    // even if websocket events are temporarily delayed.
    useEffect(() => {
        if (!token || !clientId || meters.length === 0) return;
        let cancelled = false;
        let syncInProgress = false;
        // API sync is a backup only; WebSocket is the primary real-time source.
        // Use a generous interval to avoid racing with WebSocket events.
        const currentApiSyncIntervalMs = 10000;
        const currentApiFreshnessWindowMs = Math.max(CURRENT_API_FRESHNESS_WINDOW_MS, 15000);

        const syncFromCurrentApi = async () => {
            if (syncInProgress) return;
            syncInProgress = true;
            const activeMeters = metersRef.current.filter((m) => m.isActive);
            if (activeMeters.length === 0) {
                syncInProgress = false;
                return;
            }

            try {
                const snapshots = await Promise.allSettled(
                    activeMeters.map(async (meter) => {
                        const response = await fetch(`${API_BASE}/api/clients/${clientId}/energy-meters/${meter.id}/current`, {
                            headers: { Authorization: `Bearer ${token}` },
                        });
                        if (!response.ok) {
                            throw new Error(`Failed current snapshot for meter ${meter.id}`);
                        }

                        const body = await response.json();
                        return {
                            meterId: meter.id,
                            tags: Array.isArray(body?.tags) ? body.tags : [],
                        };
                    })
                );

                if (cancelled) return;

                const updates = new Map<string, {
                    values: Record<string, number | null>;
                    latestTs: number | null;
                    kw: number | null;
                }>();

                snapshots.forEach((result) => {
                    if (result.status !== 'fulfilled') return;

                    const values: Record<string, number | null> = {};
                    let latestTs: number | null = null;
                    let kw: number | null = null;

                    for (const tag of result.value.tags) {
                        const rawValue = tag?.value;
                        const numericValue = typeof rawValue === 'number'
                            ? rawValue
                            : (rawValue === null || rawValue === undefined || rawValue === '' ? null : Number(rawValue));
                        values[tag.name] = Number.isFinite(numericValue as number) ? (numericValue as number) : null;

                        const parsedTs = Date.parse(tag?.lastUpdated || '');
                        if (!Number.isNaN(parsedTs)) {
                            latestTs = latestTs === null ? parsedTs : Math.max(latestTs, parsedTs);
                        }

                        if (kw === null && tag?.unit === 'kW' && typeof values[tag.name] === 'number') {
                            kw = values[tag.name] as number;
                        }
                    }

                    // Fall back to first numeric tag value so the graph is useful
                    // for meters that only have voltage/current tags (no kW tag).
                    if (kw === null) {
                        const firstNumeric = Object.values(values).find(v => v !== null && typeof v === 'number');
                        if (typeof firstNumeric === 'number') kw = firstNumeric;
                    }

                    updates.set(result.value.meterId, { values, latestTs, kw });
                });

                if (updates.size === 0) return;

                const syncNow = Date.now();

                // Decide freshness/schedule offline grace timers in a separate pass so this
                // fallback poll shares the exact same debounce as the WebSocket status path —
                // a single slow "current value" read (e.g. this meter's turn on a shared
                // RS-485 bus hasn't come up yet) must not instantly flip status or fire an alert.
                updates.forEach((update, meterId) => {
                    const meter = metersRef.current.find((m) => m.id === meterId);
                    if (!meter) return;

                    const wsGracePeriodMs = 15000;
                    const wsRecentlyUpdated = meter.lastUpdated !== null
                        && Number.isFinite(meter.lastUpdated)
                        && (syncNow - meter.lastUpdated) <= wsGracePeriodMs;
                    if (wsRecentlyUpdated) return;

                    const hasFreshData = update.latestTs !== null
                        && Number.isFinite(update.latestTs)
                        && (syncNow - update.latestTs) <= currentApiFreshnessWindowMs;

                    if (hasFreshData) {
                        cancelMeterOfflineTimer(meterId);
                    } else {
                        scheduleMeterOffline(meterId);
                    }
                });

                setMeters((prev) => prev.map((meter) => {
                    const update = updates.get(meter.id);
                    if (!update) return meter;

                    const now = syncNow;
                    const hasFreshData = update.latestTs !== null
                        && Number.isFinite(update.latestTs)
                        && (now - update.latestTs) <= currentApiFreshnessWindowMs;

                    const mergedValues = { ...meter.liveValues, ...update.values };
                    const history = typeof update.kw === 'number'
                        ? [...meter.history, {
                            ts: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
                            kw: +update.kw.toFixed(2),
                        }].slice(-240)
                        : meter.history;

                    // If WebSocket has provided fresh data recently, skip connected-status override.
                    // The API sync is only a fallback when WebSocket is silent.
                    const wsGracePeriodMs = 15000;
                    const wsRecentlyUpdated = meter.lastUpdated !== null
                        && Number.isFinite(meter.lastUpdated)
                        && (now - meter.lastUpdated) <= wsGracePeriodMs;
                    if (wsRecentlyUpdated) {
                        return { ...meter, liveValues: mergedValues, history };
                    }

                    // Going offline is handled by the grace-period timer scheduled above
                    // (applyMeterOffline) — only apply immediate updates for the online case.
                    if (!hasFreshData || (meter.connected && !meter.reconnecting)) {
                        return { ...meter, liveValues: mergedValues, lastUpdated: update.latestTs ?? meter.lastUpdated, history };
                    }

                    const settled = settleAccumulatedDurations(meter, now);
                    return {
                        ...meter,
                        liveValues: mergedValues,
                        lastUpdated: update.latestTs ?? meter.lastUpdated,
                        history,
                        connected: true,
                        reconnecting: false,
                        uptimeMs: settled.uptimeMs,
                        downtimeMs: settled.downtimeMs,
                        lastOnlineAt: now,
                        lastDownDurationMs: meter.lastOfflineAt ? now - meter.lastOfflineAt : meter.lastDownDurationMs,
                        currentStateSince: now,
                        sessions: [
                            ...meter.sessions.map((s, i) => i === meter.sessions.length - 1 && s.to === null ? { ...s, to: now, durationMs: now - s.from } : s),
                            { type: 'online' as const, from: now, to: null, durationMs: 0 },
                        ].slice(-40),
                    };
                }));
            } finally {
                syncInProgress = false;
            }
        };

        syncFromCurrentApi();
        const intervalId = setInterval(syncFromCurrentApi, currentApiSyncIntervalMs);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [clientId, token, meterSubscriptionKey, meters.length, pollingIntervalMs, scheduleMeterOffline, cancelMeterOfflineTimer]);

    // UI clock tick keeps relative durations accurate and reactive.
    // 10-second interval avoids per-second re-renders that make uptime/downtime visually churn.
    useEffect(() => {
        const iv = setInterval(() => {
            setNowTick(Date.now());
        }, 10000);
        return () => clearInterval(iv);
    }, []);

    // Alert engine
    useEffect(() => {
        meters.forEach(m => {
            const prev = prevStatuses.current[m.id];
            const curr = meterStatus(m);
            if (prev && prev !== curr) {
                const isDown = curr === 'offline';
                const msg = isDown
                    ? `${m.name} went OFFLINE at ${fmtTs(Date.now())}`
                    : `${m.name} came back ONLINE at ${fmtTs(Date.now())}${m.lastDownDurationMs > 0 ? ` (was down ${fmtDur(m.lastDownDurationMs)})` : ''}`;
                const newAlert = { id: `${m.id}-${Date.now()}`, msg, type: (isDown ? 'down' : 'up') as 'down' | 'up', ts: Date.now(), read: false };
                setAlerts(a => [newAlert, ...a].slice(0, 60));
                if (!notifMuted) {
                    isDown ? toast.error(msg, { icon: '⚠️', duration: 6000 }) : toast.success(msg, { icon: '✅', duration: 5000 });
                }
            }
            prevStatuses.current[m.id] = curr;
        });
    }, [meters]);

    useEffect(() => {
        if (meters.length > 0 && onDataExport) {
            onDataExport(meters.map((meter) => ({
                ...meter,
                uptimeMs: getDisplayUptimeMs(dailyUsageByMeter[meter.id] || [], nowTick),
                downtimeMs: getDisplayDowntimeMs(dailyUsageByMeter[meter.id] || [], nowTick),
            })));
        }
    }, [meters, onDataExport, nowTick, dailyUsageByMeter]);

    // Derived — only count live power from connected meters; stale liveValues on
    // offline meters must never inflate totals or rankings.
    const totalKw = meters.reduce((s, m) => s + (m.connected ? getPowerKw(m) : 0), 0);
    const connectedCount = meters.filter(m => m.connected).length;
    const offlineCount = meters.filter(m => !m.connected).length;
    const unread = alerts.filter(a => !a.read).length;
    const selected = meters.find(m => m.id === selectedId) ?? meters[0] ?? null;
    // Online meters ranked by live kW first; offline meters sorted to bottom
    const ranked = [...meters].sort((a, b) => {
        if (a.connected !== b.connected) return a.connected ? -1 : 1;
        return getPowerKw(b) - getPowerKw(a);
    });
    const barData = meters.map((m, i) => ({
        name: m.name.length > 14 ? m.name.slice(0, 12) + '…' : m.name,
        kw: m.connected ? +getPowerKw(m).toFixed(2) : 0,
        color: m.connected ? METER_COLORS[i % METER_COLORS.length] : 'hsl(var(--muted-foreground) / 0.3)',
    }));

    const comparisonMetrics = useMemo(() => {
        const startOfToday = new Date(nowTick);
        startOfToday.setHours(0, 0, 0, 0);

        const rangeDays = comparisonWindow === 'weekly' ? 7 : 30;
        const currentEnd = new Date(startOfToday);
        const currentStart = new Date(startOfToday);
        currentStart.setDate(currentStart.getDate() - (rangeDays - 1));

        const previousEnd = new Date(currentStart);
        previousEnd.setDate(previousEnd.getDate() - 1);

        const previousStart = new Date(previousEnd);
        previousStart.setDate(previousStart.getDate() - (rangeDays - 1));

        const monthStart = new Date(startOfToday);
        monthStart.setDate(monthStart.getDate() - 29);

        const currentStartKey = toDayKey(currentStart);
        const currentEndKey = toDayKey(currentEnd);
        const previousStartKey = toDayKey(previousStart);
        const previousEndKey = toDayKey(previousEnd);
        const monthStartKey = toDayKey(monthStart);

        const sumRange = (points: DailyUsagePoint[], from: string, to: string) => points
            .filter((point) => point.date >= from && point.date <= to)
            .reduce((sum, point) => sum + point.kwh, 0);

        const rows = meters.map((meter) => {
            const dailyUsage = dailyUsageByMeter[meter.id] || [];
            const currentKwh = sumRange(dailyUsage, currentStartKey, currentEndKey);
            const previousKwh = sumRange(dailyUsage, previousStartKey, previousEndKey);
            const monthlyKwh = sumRange(dailyUsage, monthStartKey, currentEndKey);
            const unitRate = Math.max(0, Number(budgetSettings.unitRatePerKwh) || 0);
            const estimatedMonthlyCost = monthlyKwh * unitRate;
            const budget = Math.max(0, Number(budgetSettings.meterBudgets[meter.id]) || 0);
            const budgetUsagePct = budget > 0 ? (estimatedMonthlyCost / budget) * 100 : null;

            return {
                meterId: meter.id,
                meterName: meter.name,
                currentKwh,
                previousKwh,
                monthlyKwh,
                estimatedMonthlyCost,
                budget,
                budgetUsagePct,
                deltaPct: previousKwh > 0 ? ((currentKwh - previousKwh) / previousKwh) * 100 : null,
            };
        });

        return {
            rangeDays,
            currentLabel: `${currentStartKey} to ${currentEndKey}`,
            previousLabel: `${previousStartKey} to ${previousEndKey}`,
            rows,
            chartData: rows.map((row) => ({
                name: row.meterName.length > 14 ? `${row.meterName.slice(0, 12)}…` : row.meterName,
                current: +row.currentKwh.toFixed(2),
                previous: +row.previousKwh.toFixed(2),
            })),
            expensiveMachines: [...rows].sort((a, b) => b.estimatedMonthlyCost - a.estimatedMonthlyCost),
            totalMonthlyCost: rows.reduce((sum, row) => sum + row.estimatedMonthlyCost, 0),
            totalBudget: rows.reduce((sum, row) => sum + row.budget, 0),
        };
    }, [nowTick, comparisonWindow, meters, dailyUsageByMeter, budgetSettings.unitRatePerKwh, budgetSettings.meterBudgets]);

    const datewiseComparisonMetrics = useMemo(() => {
        const todayKey = toDayKey(nowTick);
        const selectedDayKey = reportCompareDate && reportCompareDate <= todayKey ? reportCompareDate : todayKey;
        const previousDayKey = toDayKey(new Date(parseDayKeyToStartMs(selectedDayKey, nowTick) - DAY_MS));

        const rows = meters.map((meter) => {
            const usagePoints = dailyUsageByMeter[meter.id] || [];
            const selectedKwh = usagePoints.find((point) => point.date === selectedDayKey)?.kwh ?? 0;
            const previousKwh = usagePoints.find((point) => point.date === previousDayKey)?.kwh ?? 0;

            const selectedDurations = { 
                uptimeMs: usagePoints.find((point) => point.date === selectedDayKey)?.uptimeMs ?? 0, 
                downtimeMs: usagePoints.find((point) => point.date === selectedDayKey)?.downtimeMs ?? 0 
            };
            const previousDurations = { 
                uptimeMs: usagePoints.find((point) => point.date === previousDayKey)?.uptimeMs ?? 0, 
                downtimeMs: usagePoints.find((point) => point.date === previousDayKey)?.downtimeMs ?? 0 
            };

            const selectedTotal = selectedDurations.uptimeMs + selectedDurations.downtimeMs;
            const previousTotal = previousDurations.uptimeMs + previousDurations.downtimeMs;

            return {
                meterId: meter.id,
                meterName: meter.name,
                selectedKwh,
                previousKwh,
                selectedUptimeMs: selectedDurations.uptimeMs,
                selectedDowntimeMs: selectedDurations.downtimeMs,
                previousUptimeMs: previousDurations.uptimeMs,
                previousDowntimeMs: previousDurations.downtimeMs,
                selectedUpPct: selectedTotal > 0 ? (selectedDurations.uptimeMs / selectedTotal) * 100 : 0,
                previousUpPct: previousTotal > 0 ? (previousDurations.uptimeMs / previousTotal) * 100 : 0,
                deltaKwhPct: previousKwh > 0 ? ((selectedKwh - previousKwh) / previousKwh) * 100 : null,
            };
        });

        return {
            selectedDayKey,
            previousDayKey,
            rows,
            chartData: rows.map((row) => ({
                name: row.meterName.length > 14 ? `${row.meterName.slice(0, 12)}…` : row.meterName,
                selected: +row.selectedKwh.toFixed(2),
                previous: +row.previousKwh.toFixed(2),
            })),
        };
    }, [reportCompareDate, nowTick, meters, dailyUsageByMeter]);

    const moneyFormatter = useMemo(() => {
        const code = (budgetSettings.currencyCode || 'BDT').toUpperCase().slice(0, 3);
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: code,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        } catch {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        }
    }, [budgetSettings.currencyCode]);

    const exportCSV = () => {
        const hdrs = ['Meter', 'Status', 'kW', 'kWh', 'Voltage V', 'Current A', 'PF', 'Today Uptime', 'Today Downtime', 'Today Up%', 'Yesterday Uptime', 'Yesterday Downtime', 'Yesterday Up%', 'Last Online', 'Last Offline', 'Last Down For'];
        const rows = meters.map(m => {
            const daily = getDailyDurations(dailyUsageByMeter[m.id] || [], nowTick);
            const todayUptimeMs = daily.today.uptimeMs;
            const todayDowntimeMs = daily.today.downtimeMs;
            const yesterdayUptimeMs = daily.yesterday.uptimeMs;
            const yesterdayDowntimeMs = daily.yesterday.downtimeMs;
            return [
                m.name,
                meterStatus(m),
                getPowerKw(m).toFixed(2),
                (getEnergy(m) ?? '—'),
                (getVoltage(m) ?? '—'),
                (getCurrent(m) ?? '—'),
                (getPF(m) ?? '—'),
                fmtDur(todayUptimeMs),
                fmtDur(todayDowntimeMs),
                `${daily.todayUptimePct.toFixed(1)}%`,
                fmtDur(yesterdayUptimeMs),
                fmtDur(yesterdayDowntimeMs),
                `${daily.yesterdayUptimePct.toFixed(1)}%`,
                fmtDateTime(m.lastOnlineAt),
                fmtDateTime(m.lastOfflineAt),
                m.lastDownDurationMs > 0 ? fmtDur(m.lastDownDurationMs) : '—',
            ];
        });
        const csv = [hdrs, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
        const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })), download: `energy_meters_${new Date().toISOString().slice(0, 10)}.csv` });
        a.click(); toast.success('CSV downloaded');
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
                <p className="text-sm text-muted-foreground">Loading energy meters…</p>
            </div>
        );
    }

    if (meters.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center">
                    <Zap className="h-8 w-8 text-indigo-400 opacity-50" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold mb-1">No Energy Meters Configured</h3>
                    <p className="text-sm text-muted-foreground max-w-xs">
                        Ask your administrator to add and connect an energy meter from the admin panel.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5 mt-2">
            {/* Section header */}
            <div className="relative overflow-hidden rounded-2xl border border-indigo-500/20 bg-gradient-to-r from-indigo-50 via-violet-50 to-white dark:from-indigo-950/60 dark:via-violet-950/50 dark:to-slate-900/70 p-5">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(99,102,241,0.18),transparent_70%)]" />
                <div className="relative flex flex-col md:flex-row md:items-center gap-3 justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                            <Factory className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">Energy Meter Monitor</h3>
                            <p className="text-xs text-indigo-700/80 dark:text-indigo-300/80">Live readings · peak tracking · uptime/downtime · alerts</p>
                        </div>
                        <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-[10px] uppercase tracking-widest ml-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse mr-1 inline-block" />Live
                        </Badge>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {(['overview', 'timeline', 'report'] as const).map(t => (
                            <button key={t} onClick={() => setActiveTab(t)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${activeTab === t ? 'bg-indigo-500 text-white shadow' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-white/10 dark:text-indigo-200 dark:hover:bg-white/15'}`}>
                                {t}
                            </button>
                        ))}
                        <button onClick={() => { setShowNotif(p => !p); setAlerts(a => a.map(x => ({ ...x, read: true }))); }}
                            className="relative w-9 h-9 rounded-lg bg-indigo-100 hover:bg-indigo-200 dark:bg-white/10 dark:hover:bg-white/15 flex items-center justify-center transition-all">
                            <Bell className="h-4 w-4 text-indigo-700 dark:text-indigo-200" />
                            {unread > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-[9px] text-white flex items-center justify-center font-bold">{unread}</span>}
                        </button>
                        <button onClick={() => setNotifMuted(p => !p)} title={notifMuted ? 'Unmute' : 'Mute alerts'}
                            className="w-9 h-9 rounded-lg bg-indigo-100 hover:bg-indigo-200 dark:bg-white/10 dark:hover:bg-white/15 flex items-center justify-center transition-all">
                            {notifMuted ? <BellOff className="h-4 w-4 text-indigo-400 dark:text-indigo-300/50" /> : <Bell className="h-4 w-4 text-green-400" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { label: 'Total Power Draw', value: `${totalKw.toFixed(2)} kW`, sub: `${meters.length} meter${meters.length !== 1 ? 's' : ''}`, icon: Zap, c: 'blue' },
                    { label: 'Meters Connected', value: `${connectedCount} / ${meters.length}`, sub: 'bridge active', icon: Wifi, c: 'emerald' },
                    { label: 'Meters Offline', value: `${offlineCount}`, sub: offlineCount > 0 ? 'check connection' : 'all healthy', icon: WifiOff, c: offlineCount > 0 ? 'rose' : 'slate' },
                    { label: 'Active Alerts', value: `${unread}`, sub: 'unread', icon: AlertTriangle, c: unread > 0 ? 'amber' : 'slate' },
                ].map((k, i) => (
                    <motion.div key={k.label} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}>
                        <Card className={`border-${k.c}-500/20 bg-gradient-to-br from-${k.c}-500/5 to-transparent`}>
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl bg-${k.c}-500/15 flex items-center justify-center shrink-0`}>
                                    <k.icon className={`h-5 w-5 text-${k.c}-400`} />
                                </div>
                                <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{k.label}</p>
                                    <p className={`text-xl font-bold text-${k.c}-400`}>{k.value}</p>
                                    <p className="text-[10px] text-muted-foreground">{k.sub}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                ))}
            </div>

            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                        {meters.map((m, i) => {
                            const daily = getDailyDurations(dailyUsageByMeter[m.id] || [], nowTick);
                            const yUptimeMs = daily.yesterday.uptimeMs;
                            const yDowntimeMs = daily.yesterday.downtimeMs;

                            // Today's uptime = API historical data (from DB readings)
                            // + live session uptime accumulated since this page loaded.
                            // The API captures the period before login; live sessions capture
                            // after login. These are disjoint so we can add them safely.
                            const apiTodayUptimeMs = daily.today.uptimeMs;
                            const firstSessionFrom = m.sessions[0]?.from ?? nowTick;
                            const liveUptimeMs = getRangeDurations(m, firstSessionFrom, nowTick, nowTick).uptimeMs;
                            const uptimeMs = apiTodayUptimeMs + liveUptimeMs;
                            const todayWindowMs = Math.max(0, nowTick - getDayStartMs(nowTick));
                            const downtimeMs = Math.max(0, todayWindowMs - uptimeMs);
                            const tot = uptimeMs + downtimeMs;
                            const upPct = tot > 0 ? (uptimeMs / tot) * 100 : 0;
                            const isSelected = expandedCard === m.id;
                            const status = meterStatus(m);
                            const kw = getPowerKw(m);
                            const voltage = getVoltage(m);
                            const current = getCurrent(m);
                            const energy = getEnergy(m);
                            const pf = getPF(m);
                            const powerTagName = m.tags.find(t => t.unit === 'kW')?.name ?? '';
                            const peakKw = m.peakValues[powerTagName] ?? 0;
                            return (
                                <motion.div key={m.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                                    className={`rounded-2xl border transition-all duration-300 overflow-hidden ${isSelected ? 'border-indigo-500/50 shadow-lg shadow-indigo-500/10' : 'border-border/50 bg-card/60 hover:border-border/80'}`}>
                                    <div className="p-4 cursor-pointer" onClick={() => setExpandedCard(isSelected ? null : m.id)}>
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="w-2.5 h-2.5 rounded-full animate-pulse shrink-0" style={{ background: STATUS_COLOR[status] }} />
                                                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: STATUS_COLOR[status] }}>{status}</span>
                                                {!m.isActive && <Badge className="text-[9px] bg-slate-500/15 text-slate-400 border-0">inactive</Badge>}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <span className="text-[9px] text-muted-foreground">since {fmtTs(m.currentStateSince)}</span>
                                                {isSelected ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground ml-1" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground ml-1" />}
                                            </div>
                                        </div>
                                        <p className="font-semibold text-sm mb-0.5">{m.name}</p>
                                        {m.description && <p className="text-[10px] text-muted-foreground mb-2">{m.description}</p>}
                                        <div className="flex items-end justify-between mb-2">
                                            <div>
                                                <p className="text-[10px] text-muted-foreground">Active Power</p>
                                                <p className="text-2xl font-bold" style={{ color: METER_COLORS[i % METER_COLORS.length] }}>
                                                    {m.connected ? kw.toFixed(2) : '—'}<span className="text-xs ml-1 text-muted-foreground font-normal">kW</span>
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <div>
                                                    <p className="text-[10px] text-muted-foreground">kWh</p>
                                                    <p className="text-sm font-bold">{fmtKwh(energy)}<span className="text-xs ml-1 text-muted-foreground font-normal">kWh</span></p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-1.5 mb-2">
                                            <div className="p-2 rounded-lg bg-muted/40 border border-border/30">
                                                <p className="text-[9px] text-muted-foreground">Voltage</p>
                                                <p className="text-xs font-bold">{m.connected && voltage !== null ? `${voltage.toFixed(1)} V` : '—'}</p>
                                            </div>
                                            <div className="p-2 rounded-lg bg-muted/40 border border-border/30">
                                                <p className="text-[9px] text-muted-foreground">Current</p>
                                                <p className="text-xs font-bold">{m.connected && current !== null ? `${current.toFixed(1)} A` : '—'}</p>
                                            </div>
                                        </div>
                                        {m.history.length > 3 && (
                                            <div className="mb-2">
                                                <ResponsiveContainer width="100%" height={48}>
                                                    <LineChart data={m.history} margin={{ top: 2, right: 2, left: -30, bottom: 0 }}>
                                                        <Line type="monotone" dataKey="kw" stroke={METER_COLORS[i % METER_COLORS.length]} strokeWidth={1.5} dot={false} />
                                                        <YAxis domain={['auto', 'auto']} tick={false} axisLine={false} tickLine={false} />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}
                                        <div>
                                            <div className="flex justify-between text-[9px] text-muted-foreground mb-1">
                                                <span>↑ {fmtDur(uptimeMs)}</span><span>↓ {fmtDur(downtimeMs)}</span>
                                            </div>
                                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                                                <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-1000" style={{ width: `${upPct}%` }} />
                                            </div>
                                            <p className="text-[9px] text-right text-muted-foreground mt-0.5">Today {upPct.toFixed(0)}% uptime</p>
                                            <p className="text-[9px] text-right text-muted-foreground">Yesterday ↑ {fmtDur(yUptimeMs)} · ↓ {fmtDur(yDowntimeMs)}</p>
                                        </div>
                                    </div>
                                    <AnimatePresence>
                                        {isSelected && (
                                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                                                <div className="px-4 pb-4 border-t border-border/40 pt-3 space-y-3">
                                                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Last Event Timestamps</p>
                                                    <div className="grid grid-cols-1 gap-2">
                                                        {[
                                                            { label: 'Last came ONLINE', value: fmtDateTime(m.lastOnlineAt), icon: Power, c: 'emerald' },
                                                            { label: 'Last went OFFLINE', value: fmtDateTime(m.lastOfflineAt), icon: PowerOff, c: 'rose' },
                                                            { label: 'Last downtime duration', value: m.lastDownDurationMs > 0 ? fmtDur(m.lastDownDurationMs) : 'No downtime recorded', icon: Clock, c: 'amber' },
                                                        ].map(ev => (
                                                            <div key={ev.label} className={`flex items-center gap-2 p-2.5 rounded-xl bg-${ev.c}-500/8 border border-${ev.c}-500/15`}>
                                                                <ev.icon className={`h-3.5 w-3.5 text-${ev.c}-400 shrink-0`} />
                                                                <div>
                                                                    <p className="text-[9px] text-muted-foreground">{ev.label}</p>
                                                                    <p className={`text-xs font-semibold text-${ev.c}-400`}>{ev.value}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Live Tag Values</p>
                                                    <div className="grid grid-cols-2 gap-1.5">
                                                        {m.tags.map(tag => {
                                                            const val = m.liveValues[tag.name];
                                                            const peak = m.peakValues[tag.name];
                                                            return (
                                                                <div key={tag.id} className="p-2 rounded-lg bg-muted/40 border border-border/30">
                                                                    <p className="text-xs font-semibold text-muted-foreground truncate">{tag.name}</p>
                                                                    <p className="text-xs font-bold">{val !== null && val !== undefined ? `${+Number(val).toFixed(3)} ${tag.unit}` : '—'}</p>
                                                                    {peak !== undefined && <p className="text-[9px] text-amber-400">↑ {+Number(peak).toFixed(3)} {tag.unit}</p>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">Session History (latest 8)</p>
                                                        <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                                                            {[...m.sessions].reverse().slice(0, 8).map((s, si) => (
                                                                <div key={si} className={`flex items-center justify-between text-[10px] px-2.5 py-1.5 rounded-lg border ${s.type === 'online' ? 'bg-emerald-500/6 border-emerald-500/15' : 'bg-red-500/6 border-red-500/15'}`}>
                                                                    <div className="flex items-center gap-1.5">
                                                                        {s.type === 'online' ? <CheckCircle2 className="h-3 w-3 text-emerald-400" /> : <PowerOff className="h-3 w-3 text-red-400" />}
                                                                        <span className={s.type === 'online' ? 'text-emerald-300' : 'text-red-300'}>{s.type === 'online' ? 'Online' : 'Offline'}</span>
                                                                        <span className="text-muted-foreground ml-1">{fmtTs(s.from)}</span>
                                                                    </div>
                                                                    <span className="text-muted-foreground">{s.to ? `→ ${fmtTs(s.to)} (${fmtDur(s.durationMs)})` : <span className="text-indigo-400 font-semibold">ongoing ({fmtDur(Math.max(0, nowTick - s.from))})</span>}</span>
                                                                </div>
                                                            ))}
                                                            {m.sessions.length === 0 && <p className="text-[10px] text-muted-foreground text-center py-2">No sessions yet</p>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            );
                        })}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card className="border-border/60">
                            <CardHeader className="pb-2">
                                <div className="flex items-center gap-2">
                                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                                    <CardTitle className="text-sm font-semibold">Live Power Ranking (kW)</CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <ResponsiveContainer width="100%" height={Math.max(120, meters.length * 36)}>
                                    <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 10, left: -10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} />
                                        <XAxis type="number" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} unit=" kW" />
                                        <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} width={90} />
                                        <Tooltip formatter={(v: any) => [`${v} kW`, 'Power']} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                                        <Bar dataKey="kw" radius={[0, 4, 4, 0]}>
                                            {barData.map((d, idx) => <Cell key={idx} fill={d.color} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>

                        <Card className="border-border/60">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold">Consumption Rank & Status</CardTitle>
                                <CardDescription className="text-xs">Sorted by current power draw · click a card above to expand</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {ranked.map((m, i) => {
                                    const daily = getDailyDurations(dailyUsageByMeter[m.id] || [], nowTick);
                                    const uptimeMs = daily.today.uptimeMs;
                                    const downtimeMs = daily.today.downtimeMs;
                                    const tot = uptimeMs + downtimeMs;
                                    const upPct = tot > 0 ? ((uptimeMs / tot) * 100).toFixed(0) : '—';
                                    const status = meterStatus(m);
                                    const kw = getPowerKw(m);
                                    return (
                                        <div key={m.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-muted/25 border border-border/25 hover:border-border/50 transition-all cursor-pointer" onClick={() => setExpandedCard(m.id)}>
                                            <span className={`text-xs font-bold w-5 text-center ${i === 0 ? 'text-amber-400' : i === 1 ? 'text-slate-300' : 'text-orange-700'}`}>#{i + 1}</span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold truncate">{m.name}</p>
                                                <p className="text-[9px] text-muted-foreground">{status} · today up {upPct}% · last off {fmtTs(m.lastOfflineAt)}</p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="text-xs font-bold" style={{ color: METER_COLORS[meters.indexOf(m) % METER_COLORS.length] }}>
                                                    {m.connected ? `${kw.toFixed(2)} kW` : <span className="text-muted-foreground text-[10px]">offline</span>}
                                                </p>
                                                {getEnergy(m) !== null && <p className="text-[9px] text-muted-foreground">{fmtKwh(getEnergy(m))} kWh</p>}
                                            </div>
                                            {i === 0 && m.connected && <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-0 shrink-0">Peak</Badge>}
                                            {!m.connected && <Badge className="text-[9px] bg-red-500/15 text-red-400 border-0 shrink-0">Offline</Badge>}
                                        </div>
                                    );
                                })}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {/* TIMELINE TAB */}
            {activeTab === 'timeline' && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">Select meter:</span>
                        {meters.map(m => (
                            <button key={m.id} onClick={() => setSelectedId(m.id)}
                                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${selectedId === m.id ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}>
                                {m.name.length > 18 ? m.name.slice(0, 16) + '…' : m.name}
                            </button>
                        ))}
                    </div>
                    {selected && (
                        <>
                            {(() => {
                                const selectedDaily = getDailyDurations(dailyUsageByMeter[selected.id] || [], nowTick);
                                const selApiTodayUptimeMs = selectedDaily.today.uptimeMs;
                                const selFirstSessionFrom = selected.sessions[0]?.from ?? nowTick;
                                const selLiveUptimeMs = getRangeDurations(selected, selFirstSessionFrom, nowTick, nowTick).uptimeMs;
                                const selTodayUptimeMs = selApiTodayUptimeMs + selLiveUptimeMs;
                                const selTodayWindowMs = Math.max(0, nowTick - getDayStartMs(nowTick));
                                const selTodayDowntimeMs = Math.max(0, selTodayWindowMs - selTodayUptimeMs);
                                const selTodayTot = selTodayUptimeMs + selTodayDowntimeMs;
                                const selTodayPct = selTodayTot > 0 ? (selTodayUptimeMs / selTodayTot) * 100 : 0;
                                return (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {[
                                    { label: 'Last Came ONLINE', value: fmtDateTime(selected.lastOnlineAt), sub: selected.lastOnlineAt ? `${fmtDur(nowTick - selected.lastOnlineAt)} ago` : '', icon: Power, c: 'emerald' },
                                    { label: 'Last Went OFFLINE', value: fmtDateTime(selected.lastOfflineAt), sub: selected.lastOfflineAt ? `${fmtDur(nowTick - selected.lastOfflineAt)} ago` : '', icon: PowerOff, c: 'rose' },
                                    { label: 'Last Downtime Duration', value: selected.lastDownDurationMs > 0 ? fmtDur(selected.lastDownDurationMs) : 'No downtime recorded', sub: 'time meter was offline', icon: Clock, c: 'amber' },
                                    {
                                        label: 'Today / Yesterday Uptime',
                                        value: `${selTodayPct.toFixed(1)}% / ${selectedDaily.yesterdayUptimePct.toFixed(1)}%`,
                                        sub: `Today ↑ ${fmtDur(selTodayUptimeMs)} ↓ ${fmtDur(selTodayDowntimeMs)} | Yesterday ↑ ${fmtDur(selectedDaily.yesterday.uptimeMs)} ↓ ${fmtDur(selectedDaily.yesterday.downtimeMs)}`,
                                        icon: Activity,
                                        c: 'indigo'
                                    },
                                ].map(ev => (
                                    <Card key={ev.label} className={`border-${ev.c}-500/20 bg-gradient-to-br from-${ev.c}-500/5 to-transparent`}>
                                        <CardContent className="p-4 flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-xl bg-${ev.c}-500/15 flex items-center justify-center shrink-0`}>
                                                <ev.icon className={`h-5 w-5 text-${ev.c}-400`} />
                                            </div>
                                            <div>
                                                <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{ev.label}</p>
                                                <p className={`text-sm font-bold text-${ev.c}-400`}>{ev.value}</p>
                                                {ev.sub && <p className="text-[10px] text-muted-foreground">{ev.sub}</p>}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                                );
                            })()}

                            <Card className="border-indigo-500/20">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold">Power Trend — {selected.name}</CardTitle>
                                    <CardDescription className="text-xs">
                                        Live kW readings from bridge · rolling window
                                        {(() => { const pk = selected.tags.find(t => t.unit === 'kW'); return pk && selected.peakValues[pk.name] ? ` · Peak: ${selected.peakValues[pk.name].toFixed(2)} kW` : ''; })()}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {selected.history.length > 1 ? (
                                        <ResponsiveContainer width="100%" height={210}>
                                            <AreaChart data={selected.history} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="pwrG" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} />
                                                <XAxis dataKey="ts" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} interval={Math.floor(selected.history.length / 6)} />
                                                <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} unit=" kW" />
                                                <Tooltip formatter={(v: any) => [`${Number(v).toFixed(2)} kW`, 'Power']} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                                                <Area type="monotone" dataKey="kw" stroke="#6366f1" strokeWidth={2} fill="url(#pwrG)" name="Power kW" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center h-[180px] text-muted-foreground gap-2">
                                            <Activity className="h-8 w-8 opacity-30" />
                                            <p className="text-xs">Waiting for live data from bridge…</p>
                                            {!selected.connected && <p className="text-[10px] text-red-400">Meter is offline</p>}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="border-border/60">
                                <CardHeader className="pb-2">
                                    <div className="flex items-center gap-2">
                                        <Activity className="h-4 w-4 text-muted-foreground" />
                                        <CardTitle className="text-sm font-semibold">Full Session Log — {selected.name}</CardTitle>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                                        {[...selected.sessions].reverse().map((s, si) => (
                                            <div key={si} className={`flex items-center justify-between text-xs px-3 py-2 rounded-xl border ${s.type === 'online' ? 'bg-emerald-500/6 border-emerald-500/15' : 'bg-red-500/6 border-red-500/15'}`}>
                                                <div className="flex items-center gap-2">
                                                    {s.type === 'online' ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> : <PowerOff className="h-3.5 w-3.5 text-red-400 shrink-0" />}
                                                    <span className={`font-semibold ${s.type === 'online' ? 'text-emerald-400' : 'text-red-400'}`}>{s.type === 'online' ? 'Online' : 'Offline'}</span>
                                                    <span className="text-muted-foreground">from {fmtDateTime(s.from)}</span>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    {s.to ? <><span className="text-muted-foreground">to {fmtTs(s.to)}</span><span className={`ml-3 font-semibold ${s.type === 'offline' ? 'text-red-400' : 'text-emerald-400'}`}>{fmtDur(s.durationMs)}</span></> : <span className="text-indigo-400 font-semibold animate-pulse">● ongoing ({fmtDur(Math.max(0, nowTick - s.from))})</span>}
                                                </div>
                                            </div>
                                        ))}
                                        {selected.sessions.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No session data yet</p>}
                                    </div>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </div>
            )}

            {/* REPORT TAB */}
            {activeTab === 'report' && (
                <Card className="border-border/60">
                    <CardHeader className="pb-3 border-b border-border/40">
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="text-sm font-semibold">Energy Meter Full Report</CardTitle>
                                <CardDescription className="text-xs">Live snapshot · refreshes on each bridge reading</CardDescription>
                            </div>
                            <div className="flex gap-2">
                                <Badge className="text-[10px] gap-1"><RefreshCw className="h-2.5 w-2.5 animate-spin" />Live</Badge>
                                {onOpenDetailedReport && (
                                    <Button size="sm" variant="outline" onClick={onOpenDetailedReport} className="h-7 text-xs gap-1">
                                        <BarChart3 className="h-3 w-3" />Detailed Reports
                                    </Button>
                                )}
                                {permissions?.energyConsumption?.download && (
                                    <Button size="sm" onClick={exportCSV} className="h-7 text-xs gap-1 bg-indigo-500 hover:bg-indigo-600 border-0">
                                        <Download className="h-3 w-3" />CSV
                                    </Button>
                                )}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-3 space-y-4">
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                            <Card className="xl:col-span-2 border-border/50">
                                <CardHeader className="pb-2">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div>
                                            <CardTitle className="text-sm font-semibold">Weekly / Monthly Usage Comparison</CardTitle>
                                            <CardDescription className="text-xs">
                                                Current {comparisonMetrics.rangeDays}-day usage vs previous {comparisonMetrics.rangeDays}-day window
                                            </CardDescription>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                onClick={() => setComparisonWindow('weekly')}
                                                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${comparisonWindow === 'weekly' ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground'}`}
                                            >
                                                Weekly
                                            </button>
                                            <button
                                                onClick={() => setComparisonWindow('monthly')}
                                                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${comparisonWindow === 'monthly' ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground'}`}
                                            >
                                                Monthly
                                            </button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <p className="text-[10px] text-muted-foreground">Current: {comparisonMetrics.currentLabel} | Previous: {comparisonMetrics.previousLabel}</p>
                                    {historyLoading ? (
                                        <div className="h-[230px] flex items-center justify-center text-xs text-muted-foreground">Loading historical meter usage…</div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={230}>
                                            <BarChart data={comparisonMetrics.chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} />
                                                <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                                                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} unit=" kWh" />
                                                <Tooltip formatter={(value: any) => [`${Number(value).toFixed(2)} kWh`, 'Usage']} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                                                <Bar dataKey="current" name="Current Window" fill="#6366f1" radius={[4, 4, 0, 0]} />
                                                <Bar dataKey="previous" name="Previous Window" fill="#22c55e" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="border-border/50">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold">Budget Inputs</CardTitle>
                                    <CardDescription className="text-xs">Owner / supervisor can set unit rate and per-machine monthly budget</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="space-y-1">
                                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Unit Rate per kWh</p>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={budgetSettings.unitRatePerKwh}
                                            disabled={!canEditBudget || budgetSettingsLoading}
                                            onChange={(e) => handleBudgetSettingChange({ unitRatePerKwh: Math.max(0, Number(e.target.value) || 0) })}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Currency (ISO code)</p>
                                        <Input
                                            value={budgetSettings.currencyCode}
                                            disabled={!canEditBudget || budgetSettingsLoading}
                                            onChange={(e) => handleBudgetSettingChange({ currencyCode: e.target.value.toUpperCase().slice(0, 3) })}
                                        />
                                    </div>
                                    <div className="rounded-lg border border-border/50 p-2.5 bg-muted/30">
                                        <p className="text-[10px] text-muted-foreground">Fleet monthly cost (last 30 days)</p>
                                        <p className="text-sm font-semibold">{moneyFormatter.format(comparisonMetrics.totalMonthlyCost)}</p>
                                        <p className="text-[10px] text-muted-foreground mt-1">Fleet budget total: {moneyFormatter.format(comparisonMetrics.totalBudget)}</p>
                                        {comparisonMetrics.totalBudget > 0 && (
                                            <p className={`text-[10px] mt-1 font-semibold ${comparisonMetrics.totalMonthlyCost > comparisonMetrics.totalBudget ? 'text-red-400' : 'text-emerald-400'}`}>
                                                {((comparisonMetrics.totalMonthlyCost / comparisonMetrics.totalBudget) * 100).toFixed(1)}% of budget used
                                            </p>
                                        )}
                                    </div>
                                    {!canEditBudget && (
                                        <p className="text-[10px] text-amber-400">Read-only: ask owner/supervisor/admin account to update budget inputs.</p>
                                    )}
                                    {budgetSettingsLoading && (
                                        <p className="text-[10px] text-muted-foreground">Loading shared budget settings from server…</p>
                                    )}
                                    {budgetSettingsSaving && (
                                        <p className="text-[10px] text-indigo-300">Saving budget settings to database…</p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        <Card className="border-border/50">
                            <CardHeader className="pb-2">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <CardTitle className="text-sm font-semibold">Datewise Comparison (with Previous Day)</CardTitle>
                                        <CardDescription className="text-xs">Choose any date to compare against its previous date for kWh and uptime/downtime</CardDescription>
                                    </div>
                                    <div className="w-44">
                                        <Input
                                            type="date"
                                            max={toDayKey(nowTick)}
                                            value={reportCompareDate}
                                            onChange={(e) => setReportCompareDate(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <p className="text-[10px] text-muted-foreground">
                                    Selected: {datewiseComparisonMetrics.selectedDayKey} | Previous: {datewiseComparisonMetrics.previousDayKey}
                                </p>
                                {historyLoading ? (
                                    <div className="h-[210px] flex items-center justify-center text-xs text-muted-foreground">Loading datewise comparison data…</div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={210}>
                                        <BarChart data={datewiseComparisonMetrics.chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} />
                                            <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                                            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} unit=" kWh" />
                                            <Tooltip formatter={(value: any) => [`${Number(value).toFixed(2)} kWh`, 'Usage']} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                                            <Bar dataKey="selected" name={`Selected (${datewiseComparisonMetrics.selectedDayKey})`} fill="#6366f1" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="previous" name={`Previous (${datewiseComparisonMetrics.previousDayKey})`} fill="#22c55e" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}

                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="border-b border-border/40">
                                                {['Meter', 'Sel kWh', 'Prev kWh', 'Δ kWh%', 'Sel Uptime', 'Sel Downtime', 'Sel Up%', 'Prev Uptime', 'Prev Downtime', 'Prev Up%'].map((header) => (
                                                    <th key={header} className="text-left py-2 px-2 font-semibold text-muted-foreground text-[11px] uppercase tracking-wider whitespace-nowrap">{header}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {datewiseComparisonMetrics.rows.map((row) => (
                                                <tr key={row.meterId} className="border-b border-border/20 hover:bg-muted/20 transition-colors">
                                                    <td className="py-2.5 px-2 font-semibold whitespace-nowrap">{row.meterName}</td>
                                                    <td className="py-2.5 px-2">{row.selectedKwh.toFixed(2)}</td>
                                                    <td className="py-2.5 px-2">{row.previousKwh.toFixed(2)}</td>
                                                    <td className="py-2.5 px-2">
                                                        {row.deltaKwhPct === null
                                                            ? '—'
                                                            : <span className={row.deltaKwhPct >= 0 ? 'text-amber-400 font-semibold' : 'text-emerald-400 font-semibold'}>{`${row.deltaKwhPct >= 0 ? '+' : ''}${row.deltaKwhPct.toFixed(1)}%`}</span>}
                                                    </td>
                                                    <td className="py-2.5 px-2 text-emerald-400">{fmtDur(row.selectedUptimeMs)}</td>
                                                    <td className="py-2.5 px-2 text-red-400">{fmtDur(row.selectedDowntimeMs)}</td>
                                                    <td className="py-2.5 px-2 text-emerald-400 font-semibold">{row.selectedUpPct.toFixed(1)}%</td>
                                                    <td className="py-2.5 px-2 text-emerald-400">{fmtDur(row.previousUptimeMs)}</td>
                                                    <td className="py-2.5 px-2 text-red-400">{fmtDur(row.previousDowntimeMs)}</td>
                                                    <td className="py-2.5 px-2 text-emerald-400 font-semibold">{row.previousUpPct.toFixed(1)}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-border/40">
                                    {['Meter', 'Status', 'kW', 'kWh', 'V', 'A', 'PF', 'Today Uptime', 'Today Downtime', 'Today Up%', 'Yest. Uptime', 'Yest. Downtime', 'Yest. Up%', 'Last Online', 'Last Offline', 'Last Down'].map(h => (
                                        <th key={h} className="text-left py-2 px-2 font-semibold text-muted-foreground text-[11px] uppercase tracking-wider whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {meters.map((m, i) => {
                                    const daily = getDailyDurations(dailyUsageByMeter[m.id] || [], nowTick);
                                    const uptimeMs = daily.today.uptimeMs;
                                    const downtimeMs = daily.today.downtimeMs;
                                    const tot = uptimeMs + downtimeMs;
                                    const upPct = tot > 0 ? ((uptimeMs / tot) * 100).toFixed(1) : '—';
                                    const status = meterStatus(m);
                                    const kw = getPowerKw(m);
                                    const pf = getPF(m);
                                    return (
                                        <tr key={m.id} className="border-b border-border/20 hover:bg-muted/20 transition-colors">
                                            <td className="py-2.5 px-2 font-semibold whitespace-nowrap">{m.name}</td>
                                            <td className="py-2.5 px-2">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase" style={{ background: `${STATUS_COLOR[status]}20`, color: STATUS_COLOR[status] }}>
                                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLOR[status] }} />{status}
                                                </span>
                                            </td>
                                            <td className="py-2.5 px-2 font-bold" style={{ color: m.connected ? METER_COLORS[i % METER_COLORS.length] : undefined }}>{m.connected ? kw.toFixed(2) : '—'}</td>
                                            <td className="py-2.5 px-2">{fmtKwh(getEnergy(m))}</td>
                                            <td className="py-2.5 px-2">{getVoltage(m) !== null ? getVoltage(m)?.toFixed(1) : '—'}</td>
                                            <td className="py-2.5 px-2">{getCurrent(m) !== null ? getCurrent(m)?.toFixed(1) : '—'}</td>
                                            <td className="py-2.5 px-2">{pf !== null ? <span className={pf < 0.9 ? 'text-amber-400 font-bold' : ''}>{pf.toFixed(3)}</span> : '—'}</td>
                                            <td className="py-2.5 px-2 text-emerald-400 font-medium">{fmtDur(uptimeMs)}</td>
                                            <td className="py-2.5 px-2 text-red-400 font-medium">{fmtDur(downtimeMs)}</td>
                                            <td className="py-2.5 px-2">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden">
                                                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${upPct !== '—' ? upPct : '0'}%` }} />
                                                    </div>
                                                    <span className="text-emerald-400 font-semibold">{upPct}%</span>
                                                </div>
                                            </td>
                                            <td className="py-2.5 px-2 text-emerald-400 font-medium">{fmtDur(daily.yesterday.uptimeMs)}</td>
                                            <td className="py-2.5 px-2 text-red-400 font-medium">{fmtDur(daily.yesterday.downtimeMs)}</td>
                                            <td className="py-2.5 px-2 text-emerald-400 font-semibold">{daily.yesterdayUptimePct.toFixed(1)}%</td>
                                            <td className="py-2.5 px-2 whitespace-nowrap text-emerald-400">{fmtDateTime(m.lastOnlineAt)}</td>
                                            <td className="py-2.5 px-2 whitespace-nowrap text-red-400">{fmtDateTime(m.lastOfflineAt)}</td>
                                            <td className="py-2.5 px-2 whitespace-nowrap text-amber-400 font-medium">{m.lastDownDurationMs > 0 ? fmtDur(m.lastDownDurationMs) : '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                            <Card className="border-border/50">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold">Budget Tracker by Machine</CardTitle>
                                    <CardDescription className="text-xs">Monthly usage is based on last 30 days of kWh readings</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <div className="max-h-80 overflow-y-auto pr-1 space-y-2">
                                        {comparisonMetrics.rows.map((row) => {
                                            const overBudget = row.budget > 0 && row.estimatedMonthlyCost > row.budget;
                                            return (
                                                <div key={row.meterId} className="rounded-lg border border-border/40 bg-muted/20 p-2.5 space-y-1.5">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <p className="text-xs font-semibold truncate">{row.meterName}</p>
                                                        <span className={`text-[10px] font-semibold ${overBudget ? 'text-red-400' : 'text-emerald-400'}`}>
                                                            {row.budgetUsagePct !== null ? `${row.budgetUsagePct.toFixed(1)}%` : 'No budget'}
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-3 gap-1.5 text-[10px] text-muted-foreground">
                                                        <p>Usage: <span className="text-foreground font-medium">{row.monthlyKwh.toFixed(2)} kWh</span></p>
                                                        <p>Cost: <span className="text-foreground font-medium">{moneyFormatter.format(row.estimatedMonthlyCost)}</span></p>
                                                        <p>Budget: <span className="text-foreground font-medium">{row.budget > 0 ? moneyFormatter.format(row.budget) : '—'}</span></p>
                                                    </div>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        disabled={!canEditBudget || budgetSettingsLoading}
                                                        value={row.budget > 0 ? row.budget : ''}
                                                        placeholder="Set monthly budget"
                                                        onChange={(e) => handleMeterBudgetChange(row.meterId, e.target.value)}
                                                    />
                                                </div>
                                            );
                                        })}
                                        {comparisonMetrics.rows.length === 0 && (
                                            <p className="text-xs text-muted-foreground text-center py-4">No machine usage history available yet.</p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-border/50">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold">Most Expensive Machines</CardTitle>
                                    <CardDescription className="text-xs">Ranked by estimated monthly cost (last 30 days)</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    {comparisonMetrics.expensiveMachines.slice(0, 5).map((row, index) => (
                                        <div key={row.meterId} className="p-2.5 rounded-lg border border-border/40 bg-muted/20">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="text-xs font-semibold">#{index + 1} {row.meterName}</p>
                                                <p className="text-xs font-bold text-indigo-300">{moneyFormatter.format(row.estimatedMonthlyCost)}</p>
                                            </div>
                                            <p className="text-[10px] text-muted-foreground mt-1">
                                                {row.currentKwh.toFixed(2)} kWh in current window vs {row.previousKwh.toFixed(2)} kWh in previous window
                                                {row.deltaPct !== null ? ` (${row.deltaPct >= 0 ? '+' : ''}${row.deltaPct.toFixed(1)}%)` : ''}
                                            </p>
                                        </div>
                                    ))}
                                    {comparisonMetrics.expensiveMachines.length === 0 && (
                                        <p className="text-xs text-muted-foreground text-center py-4">No comparison data yet.</p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* NOTIFICATION PANEL */}
            <AnimatePresence>
                {showNotif && (
                    <motion.div initial={{ x: 420, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 420, opacity: 0 }} transition={{ type: 'spring', damping: 22 }}
                        className="fixed right-4 top-20 w-80 h-[calc(100vh-100px)] bg-card/97 backdrop-blur border border-border/60 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-border/40 shrink-0">
                            <div className="flex items-center gap-2">
                                <Bell className="h-4 w-4 text-indigo-400" />
                                <h3 className="text-sm font-bold">Meter Alerts</h3>
                                {unread > 0 && <span className="text-[9px] bg-red-500 text-white px-1.5 py-0.5 rounded-full font-bold">{unread}</span>}
                            </div>
                            <button onClick={() => setShowNotif(false)}><X className="h-4 w-4 text-muted-foreground hover:text-foreground" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3 space-y-2">
                            {alerts.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                                    <CheckCircle2 className="h-8 w-8 mb-2 opacity-30" />
                                    <p className="text-sm font-medium">No alerts</p>
                                    <p className="text-xs">All meters operating normally</p>
                                </div>
                            ) : alerts.map(a => (
                                <div key={a.id} className={`p-3 rounded-xl border text-xs ${a.type === 'down' ? 'border-red-500/25 bg-red-500/8' : a.type === 'up' ? 'border-emerald-500/25 bg-emerald-500/8' : 'border-amber-500/25 bg-amber-500/8'}`}>
                                    <div className="flex items-center gap-1.5 mb-1">
                                        {a.type === 'down' ? <PowerOff className="h-3 w-3 text-red-400 shrink-0" /> : a.type === 'up' ? <Power className="h-3 w-3 text-emerald-400 shrink-0" /> : <AlertTriangle className="h-3 w-3 text-amber-400 shrink-0" />}
                                        <span className="ml-auto text-[9px] text-muted-foreground">{fmtTs(a.ts)}</span>
                                    </div>
                                    <p className={a.type === 'down' ? 'text-red-300' : a.type === 'up' ? 'text-emerald-300' : 'text-amber-300'}>{a.msg}</p>
                                </div>
                            ))}
                        </div>
                        <div className="p-3 border-t border-border/40 shrink-0">
                            <button onClick={() => setAlerts([])} className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors">Clear all alerts</button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
